import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useLang, useMatch } from "@/App";
import { UI, tr } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PreliminaryReport from "@/components/PreliminaryReport";
import { ChevronRight, Lock, RotateCcw, FileText, CalendarClock, PhoneCall, ArrowRight } from "lucide-react";
import { track as gaTrack } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getUTM = () => {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach((k) => { if (p.get(k)) utm[k] = p.get(k); });
  return utm;
};

// The free result page: concise result + 3-tier CTA hierarchy + Continue with FORINONES.
const FreeResult = ({ report, lang }) => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ website: "https://forinones.com", bookingIntro: "", bookingConsult: "" });
  const token = report.token;

  useEffect(() => { axios.get(`${API}/onematch/config`).then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {}); }, []);
  useEffect(() => { gaTrack("free_result_viewed"); }, []);

  const track = (cta) => { axios.post(`${API}/onematch/report/${token}/cta`, { cta }).catch(() => {}); };

  const unlock = () => { track("unlock_report"); gaTrack("detailed_report_clicked", { from: "free_result" }); navigate(`/onematch/report/${token}/detailed`); };

  const consult = async (type) => {
    gaTrack("consultation_clicked", { consult_type: type });
    track(type === "intro" ? "intro_call" : "paid_consult");
    try {
      const r = await axios.post(`${API}/onematch/report/${token}/consultation-intent`, { type });
      if (r.data.bookingUrl) { window.open(r.data.bookingUrl, "_blank", "noopener"); }
      else { toast.success(tr(lang, UI.intent_recorded)); }
    } catch (e) { toast.error(tr(lang, UI.error_generic)); }
  };

  const continueForinones = () => { track("continue_forinones"); window.open(cfg.website, "_blank", "noopener"); };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 om-fade-up">
      <PreliminaryReport assessment={report.assessment} firstName={report.firstName} />

      {/* Why these ranked highest */}
      <div className="mt-10 bg-cream border border-border rounded-sm p-6">
        <h2 className="font-display text-2xl text-navy-deep mb-2">{tr(lang, UI.why_ranked)}</h2>
        <p className="text-sm text-navy-deep leading-relaxed">{tr(lang, UI.why_ranked_text)}</p>
      </div>

      {/* CTA hierarchy */}
      <div className="mt-10 space-y-4">
        {/* Primary */}
        <button data-testid="cta-unlock-report" onClick={unlock}
          className="w-full text-left bg-navy-deep text-white rounded-sm p-6 sm:p-7 relative om-grain hover:bg-navy transition-colors duration-300 group">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-gold mb-1"><FileText className="w-4 h-4" /><span className="overline">{tr(lang, UI.report_head)}</span></div>
              <h3 className="font-display text-2xl sm:text-3xl">{tr(lang, UI.cta_unlock)}</h3>
              <p className="mt-2 text-white/70 text-sm leading-relaxed max-w-xl">{tr(lang, UI.cta_unlock_sub)}</p>
            </div>
            <ArrowRight className="w-6 h-6 text-gold shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        </button>

        {/* Secondary — paid consult */}
        <button data-testid="cta-paid-consult" onClick={() => consult("consult")}
          className="w-full text-left bg-white border-2 border-navy rounded-sm p-6 hover:bg-cream transition-colors duration-300">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1"><CalendarClock className="w-4 h-4 text-navy" /><span className="text-[10px] uppercase tracking-wide bg-navy text-white rounded-full px-2 py-0.5">{tr(lang, UI.badge_paid)}</span></div>
              <h3 className="font-display text-xl sm:text-2xl text-navy-deep">{tr(lang, UI.cta_consult)}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{tr(lang, UI.cta_consult_sub)}</p>
              <p className="mt-1.5 text-xs text-navy">{tr(lang, UI.consult_deduct)}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-navy shrink-0" />
          </div>
        </button>

        {/* Alternative — free intro (visually lighter) */}
        <button data-testid="cta-intro-call" onClick={() => consult("intro")}
          className="w-full text-left bg-transparent border border-border rounded-sm p-5 hover:border-navy/50 transition-colors duration-300">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1"><PhoneCall className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-[10px] uppercase tracking-wide border border-border text-muted-foreground rounded-full px-2 py-0.5">{tr(lang, UI.badge_free)}</span></div>
              <h3 className="font-medium text-navy-deep">{tr(lang, UI.cta_intro)}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{tr(lang, UI.cta_intro_sub)}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
        </button>
      </div>

      {/* Continue with FORINONES */}
      <div className="mt-10 bg-white border border-border rounded-sm p-6 text-center">
        <h3 className="font-display text-2xl text-navy-deep">{tr(lang, UI.continue_forinones)}</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">{tr(lang, UI.continue_text)}</p>
        <button data-testid="cta-continue-forinones" onClick={continueForinones}
          className="mt-4 inline-flex items-center gap-2 bg-gold text-navy-deep px-6 py-3 rounded-sm font-bold hover:bg-navy hover:text-white transition-colors duration-300">
          {tr(lang, UI.visit_website)} <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-8 text-center">
        <button data-testid="restart-btn" onClick={() => navigate("/assessment")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-navy transition-colors duration-300 text-sm font-medium">
          <RotateCcw className="w-4 h-4" /> {tr(lang, UI.restart)}
        </button>
      </div>
      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{tr(lang, UI.report_disclaimer)}</p>
    </div>
  );
};

const Results = () => {
  const { lang } = useLang();
  const { matchData, answers } = useMatch();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", country: "", consent: false, marketingConsent: false, companyWebsite: "" });
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  if (!matchData) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">No results yet.</p>
        <button onClick={() => navigate("/assessment")} className="bg-navy text-white px-6 py-3 rounded-sm font-semibold" data-testid="no-results-start">{tr(lang, UI.start)}</button>
      </div>
    );
  }

  const top3 = matchData.results.slice(0, 3);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.country) {
      toast.error(tr(lang, UI.fields_required)); return;
    }
    if (!form.consent) { toast.error(tr(lang, UI.consent_required)); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/onematch/lead`, {
        ...form, lang, answers, nationality: (answers && answers.nationality) || "",
        topMatches: top3.map((r) => ({ code: r.code, name: r.name, flag: r.flag, overall: r.overall, whyCodes: r.whyCodes, mayNotCodes: r.mayNotCodes })),
        utm: getUTM(), landingPageUrl: window.location.href, referralUrl: document.referrer || "",
        source: new URLSearchParams(window.location.search).get("utm_source") || (document.referrer.includes("instagram") ? "Instagram" : "OneMatch"),
      });
      const rep = await axios.get(`${API}/onematch/report/${res.data.reportToken}`);
      setReport({ token: res.data.reportToken, ...rep.data });
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(tr(lang, UI.error_generic));
    }
    setBusy(false);
  };

  if (report) return <FreeResult report={report} lang={lang} />;

  // Gate FIRST — no country names or scores revealed before the lead is captured.
  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
      <div className="text-center om-fade-up">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-navy bg-gold/20 rounded-full px-3 py-1 mb-4">
          <Lock className="w-3.5 h-3.5" /> {tr(lang, UI.report_head)}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-navy-deep">{tr(lang, UI.match_ready_head)}</h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">{tr(lang, UI.match_ready_sub)}</p>
      </div>

      <div className="mt-8 bg-white border border-border rounded-sm p-6 sm:p-8">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-sm font-medium text-navy-deep">{tr(lang, UI.f_first)} *</label>
            <Input data-testid="lead-firstName" value={form.firstName} onChange={set("firstName")} className="mt-1.5 h-11 rounded-sm" /></div>
          <div><label className="text-sm font-medium text-navy-deep">{tr(lang, UI.f_last)} *</label>
            <Input data-testid="lead-lastName" value={form.lastName} onChange={set("lastName")} className="mt-1.5 h-11 rounded-sm" /></div>
        </div>
        <div className="mt-3"><label className="text-sm font-medium text-navy-deep">{tr(lang, UI.f_email)} *</label>
          <Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-sm" /></div>
        <div className="mt-3"><label className="text-sm font-medium text-navy-deep">{tr(lang, UI.f_wa)} *</label>
          <div className="mt-1.5 om-phone">
            <PhoneInput data-testid="lead-phone" international countryCallingCodeEditable={false} defaultCountry="TR" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v || "" }))} />
          </div></div>
        <div className="mt-3"><label className="text-sm font-medium text-navy-deep">{tr(lang, UI.f_residence)} *</label>
          <Input data-testid="lead-country" value={form.country} onChange={set("country")} className="mt-1.5 h-11 rounded-sm" /></div>

        {/* Honeypot — hidden from real users */}
        <input type="text" tabIndex="-1" autoComplete="off" value={form.companyWebsite}
          onChange={set("companyWebsite")} data-testid="lead-hp"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

        <label className="mt-4 flex items-start gap-3 cursor-pointer">
          <input data-testid="lead-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-1 w-4 h-4 accent-navy" />
          <span className="text-xs text-muted-foreground leading-relaxed">
            {tr(lang, UI.consent_text)}{" "}
            <button type="button" data-testid="privacy-link" onClick={() => setPrivacyOpen(true)} className="text-navy underline">{tr(lang, UI.privacy_notice)}</button>.
          </span>
        </label>

        <label className="mt-3 flex items-start gap-3 cursor-pointer">
          <input data-testid="lead-marketing" type="checkbox" checked={form.marketingConsent} onChange={(e) => setForm((f) => ({ ...f, marketingConsent: e.target.checked }))} className="mt-1 w-4 h-4 accent-navy" />
          <span className="text-xs text-muted-foreground leading-relaxed">{tr(lang, UI.opt_marketing)}</span>
        </label>

        <button data-testid="send-report-btn" onClick={submit} disabled={busy}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy text-white py-4 rounded-sm font-bold hover:bg-navy-deep transition-colors duration-300 disabled:opacity-50">
          {busy ? tr(lang, UI.saving) : tr(lang, UI.send_report)}
        </button>
        <p className="mt-3 text-center text-xs text-muted-foreground">{tr(lang, UI.gate_reassure)}</p>
      </div>

      <div className="mt-8 text-center">
        <button data-testid="restart-btn" onClick={() => navigate("/assessment")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-navy transition-colors duration-300 text-sm font-medium">
          <RotateCcw className="w-4 h-4" /> {tr(lang, UI.restart)}
        </button>
      </div>

      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="privacy-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy-deep">{tr(lang, UI.privacy_notice)}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed">{tr(lang, UI.privacy_body)}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Results;
